import { supabase } from './supabase';
import type {
  MetaAlignmentAd,
  MetaAlignmentAdSet,
  MetaAlignmentAssociation,
  MetaAlignmentCampaign,
  MetaAlignmentFilters,
  MetaAlignmentMetrics,
  MetaAlignmentResult,
  MetaAlignmentSuggestion,
  MetaAlignmentSuggestionReason,
} from '../../shared/types/metaAlignment';
import { fetchMetaInsightMetrics, normalizeMetricsRow, type MetaInsightsAggregate } from './metaMetrics';

type ReleaseContext = {
  id: string;
  title: string;
  release_date?: string | null;
  artist_id?: string | null;
  artist_name?: string | null;
  client_id?: string | null;
  client_name?: string | null;
  primary_contact_id?: string | null;
  primary_contact_name?: string | null;
};

type CampaignRow = {
  id: string;
  campaign_id: string;
  account_id: string;
  name: string;
  status: string;
  objective?: string | null;
  start_time?: string | null;
  stop_time?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
};

type AdSetRow = {
  id: string;
  adset_id: string;
  campaign_id: string;
  account_id: string;
  name: string;
  status: string;
  updated_at?: string | null;
  created_at?: string | null;
};

type AdRow = {
  id: string;
  ad_id: string;
  adset_id: string;
  campaign_id: string;
  account_id: string;
  name: string;
  status: string;
  updated_at?: string | null;
  created_at?: string | null;
};

type MetricsRow = MetaInsightsAggregate;

type AssociationRow = {
  campaign_id: string;
  entity_type: 'release' | 'artist' | 'label';
  entity_id: string;
  association_type: 'primary' | 'secondary' | 'related';
  notes?: string | null;
  created_at: string;
};

const DEFAULT_LIMIT = 40;
const MAX_ADSETS = 200;
const MAX_ADS = 400;
const DEFAULT_SUGGESTION_LIMIT = 3;

const STOPWORDS = new Set(['the', 'and', 'for', 'with', 'feat', 'ft', 'remix', 'edit', 'version', 'club', 'mix', 'radio', 'feat.', 'ft.']);

const normalize = (value?: string | null) => value?.trim().toLowerCase() ?? '';

const tokenize = (value?: string | null): string[] => {
  const normalized = normalize(value).replace(/[^a-z0-9\s]/g, ' ');
  return normalized
    .split(/\s+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0 && !STOPWORDS.has(part));
};

const buildMetrics = (row?: MetricsRow | null): MetaAlignmentMetrics => normalizeMetricsRow(row);

const computeNameMatchScore = (source: string, target?: string | null): MetaAlignmentSuggestionReason | null => {
  if (!target) return null;
  const sourceTokens = tokenize(source);
  const targetTokens = tokenize(target);
  if (!sourceTokens.length || !targetTokens.length) return null;

  const targetSet = new Set(targetTokens);
  const intersection = sourceTokens.filter((token) => targetSet.has(token));
  if (!intersection.length) return null;

  const ratio = intersection.length / targetTokens.length;
  const weight = Math.round(Math.min(60, ratio * 60));
  if (weight <= 0) return null;

  return {
    label: `Name overlap (${intersection.join(', ')})`,
    weight,
  };
};

const computeDateAffinityScore = (campaign: CampaignRow, release?: ReleaseContext): MetaAlignmentSuggestionReason | null => {
  if (!release?.release_date) return null;
  const releaseDate = new Date(release.release_date);
  if (Number.isNaN(releaseDate.getTime())) return null;

  const candidateDate = campaign.start_time ? new Date(campaign.start_time) : campaign.updated_at ? new Date(campaign.updated_at) : null;
  if (!candidateDate || Number.isNaN(candidateDate.getTime())) return null;

  const diffMs = Math.abs(candidateDate.getTime() - releaseDate.getTime());
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffDays <= 7) return { label: 'Launch window ±7 days', weight: 20 };
  if (diffDays <= 30) return { label: 'Launch window ±30 days', weight: 12 };
  if (diffDays <= 60) return { label: 'Launch window ±60 days', weight: 6 };
  return null;
};

const computeArtistSignal = (campaign: CampaignRow, release?: ReleaseContext): MetaAlignmentSuggestionReason | null => {
  if (!release?.artist_name) return null;
  const reason = computeNameMatchScore(campaign.name, release.artist_name);
  if (!reason) return null;
  return { label: `Artist match (${release.artist_name})`, weight: Math.round(reason.weight * 0.6) };
};

const mergeReasons = (reasons: Array<MetaAlignmentSuggestionReason | null>) =>
  reasons.filter((reason): reason is MetaAlignmentSuggestionReason => Boolean(reason));

const computeSuggestionScore = (reasons: MetaAlignmentSuggestionReason[]) =>
  reasons.reduce((total, reason) => total + reason.weight, 0);

const getPrimaryContact = (
  contacts?: Array<{
    client_contact?: {
      id: string;
      first_name: string;
      last_name: string;
      email?: string | null;
      is_primary?: boolean;
    } | null;
  }>
) => {
  const list = contacts ?? [];
  if (!list.length) return undefined;
  const primary = list.find((entry) => entry.client_contact?.is_primary);
  const chosen = primary ?? list[0];
  const contact = chosen.client_contact;
  if (!contact) return undefined;
  const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim();
  return {
    id: contact.id,
    name: name || contact.email || 'Contact',
  };
};

const mapReleaseContext = (
  rows: any[],
  contactMap: Map<string, Array<{ client_contact?: { id: string; first_name: string; last_name: string; email?: string | null; is_primary?: boolean } | null }>> = new Map()
): ReleaseContext[] => {
  return rows.map((row) => {
    const artist = row.artist as
      | {
          id: string;
          name: string;
          primary_contact_id?: string | null;
          contact?: { id: string; name: string } | null;
        }
      | null
      | undefined;
    const primaryContact = getPrimaryContact(contactMap.get(row.id) ?? (row.release_contacts as any[] | undefined));

    return {
      id: row.id,
      title: row.title,
      release_date: row.release_date,
      artist_id: artist?.id ?? null,
      artist_name: artist?.name ?? null,
      client_id: artist?.primary_contact_id ?? null,
      client_name: artist?.contact?.name ?? null,
      primary_contact_id: primaryContact?.id ?? null,
      primary_contact_name: primaryContact?.name ?? null,
    };
  });
};

const fetchReleaseContexts = async (requiredIds: string[], limit = 200): Promise<Map<string, ReleaseContext>> => {
  const contextMap = new Map<string, ReleaseContext>();

  const fetchReleases = async (ids?: string[]) => {
    let query = supabase
      .from('releases')
      .select(
        `
        id,
        title,
        release_date,
        artist_id,
        artist:artists (
          id,
          name,
          primary_contact_id,
          contact:contacts!primary_contact_id ( id, name )
        )
      `
      );
    if (ids?.length) {
      query = query.in('id', ids);
    } else {
      query = query.order('release_date', { ascending: false }).limit(limit);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Failed to fetch releases for alignment:', error);
      return [];
    }

    return data ?? [];
  };

  const fetchReleaseContactsByReleaseIds = async (
    releaseIds: string[]
  ): Promise<Map<string, Array<{ client_contact?: { id: string; first_name: string; last_name: string; email?: string | null; is_primary?: boolean } | null }>>> => {
    const map = new Map<string, Array<{ client_contact?: { id: string; first_name: string; last_name: string; email?: string | null; is_primary?: boolean } | null }>>();
    if (!releaseIds.length) return map;

    const chunkSize = 50;
    for (let i = 0; i < releaseIds.length; i += chunkSize) {
      const chunk = releaseIds.slice(i, i + chunkSize);
      const { data, error } = await supabase
        .from('entity_contacts')
        .select(
          `
          entity_id,
          is_primary,
          contact_person:contact_persons (
            id,
            first_name,
            last_name,
            email
          )
        `
        )
        .eq('entity_type', 'release')
        .in('entity_id', chunk);
      if (error) {
        console.error('Failed to fetch release contacts for alignment:', error);
        continue;
      }

      (data ?? []).forEach((row: any) => {
        const list = map.get(row.entity_id) ?? [];
        list.push({ client_contact: row.contact_person ? { ...row.contact_person, is_primary: row.is_primary } : null });
        map.set(row.entity_id, list);
      });
    }

    return map;
  };

  const recentReleases = await fetchReleases();
  const recentContacts = await fetchReleaseContactsByReleaseIds(recentReleases.map((row: any) => row.id));
  mapReleaseContext(recentReleases, recentContacts).forEach((ctx) => contextMap.set(ctx.id, ctx));

  const missingIds = requiredIds.filter((id) => !contextMap.has(id));
  if (missingIds.length) {
    const chunkSize = 50;
    for (let i = 0; i < missingIds.length; i += chunkSize) {
      const chunk = missingIds.slice(i, i + chunkSize);
      const chunkResults = await fetchReleases(chunk);
      const chunkContacts = await fetchReleaseContactsByReleaseIds(chunkResults.map((row: any) => row.id));
      mapReleaseContext(chunkResults, chunkContacts).forEach((ctx) => contextMap.set(ctx.id, ctx));
    }
  }

  return contextMap;
};

const buildAssociations = (
  rows: AssociationRow[],
  releaseContexts: Map<string, ReleaseContext>,
  artistMap: Map<string, { id: string; name: string }>,
  labelMap: Map<string, { id: string; name: string }>
): Map<string, MetaAlignmentAssociation[]> => {
  const associationsByCampaign = new Map<string, MetaAlignmentAssociation[]>();

  rows.forEach((row) => {
    const list = associationsByCampaign.get(row.campaign_id) ?? [];
    let entityName = '';
    let relatedReleaseId: string | undefined;
    let relatedArtistId: string | undefined;
    let relatedContactId: string | undefined;
    let relatedContactName: string | undefined;

    if (row.entity_type === 'release') {
      const ctx = releaseContexts.get(row.entity_id);
      entityName = ctx?.title ?? 'Unknown release';
      relatedReleaseId = ctx?.id;
      relatedArtistId = ctx?.artist_id ?? undefined;
      relatedContactId = ctx?.primary_contact_id ?? undefined;
      relatedContactName = ctx?.primary_contact_name ?? undefined;
    } else if (row.entity_type === 'artist') {
      const artist = artistMap.get(row.entity_id);
      entityName = artist?.name ?? 'Unknown artist';
      relatedArtistId = artist?.id;
    } else if (row.entity_type === 'label') {
      const label = labelMap.get(row.entity_id);
      entityName = label?.name ?? 'Unknown label';
    }

    list.push({
      entityType: row.entity_type,
      entityId: row.entity_id,
      entityName,
      associationType: row.association_type,
      notes: row.notes ?? undefined,
      createdAt: row.created_at,
      relatedReleaseId,
      relatedArtistId,
      relatedContactId,
      relatedContactName,
    });
    associationsByCampaign.set(row.campaign_id, list);
  });

  return associationsByCampaign;
};

const enrichAssociationsWithRpc = async (
  campaignIds: string[],
  associationsByCampaign: Map<string, MetaAlignmentAssociation[]>
) => {
  const pending = campaignIds
    .map((id) => id.trim())
    .filter((id) => id && associationsByCampaign.has(id))
    .filter((id, index, array) => array.indexOf(id) === index);

  if (!pending.length) return;

  const campaignsNeedingLookup = pending.filter((campaignId) => {
    const associations = associationsByCampaign.get(campaignId);
    return associations?.some((assoc) => !assoc.entityName || assoc.entityName.toLowerCase().startsWith('unknown'));
  });

  if (!campaignsNeedingLookup.length) return;

  const chunkSize = 5;
  for (let i = 0; i < campaignsNeedingLookup.length; i += chunkSize) {
    const chunk = campaignsNeedingLookup.slice(i, i + chunkSize);
    await Promise.all(
      chunk.map(async (campaignId) => {
        const { data, error } = await supabase
          .rpc('get_campaign_associations', {
            p_campaign_id: campaignId,
            p_campaign_type: 'meta',
          });

        if (error) {
          console.error('Failed to enrich campaign associations via RPC:', error);
          return;
        }

        const rpcAssociations = Array.isArray(data) ? data : [];
        if (!rpcAssociations.length) return;

        const associations = associationsByCampaign.get(campaignId);
        if (!associations) return;

        rpcAssociations.forEach((rpcAssoc: any) => {
          const match = associations.find(
            (assoc) =>
              assoc.entityType === rpcAssoc.entity_type &&
              assoc.entityId === rpcAssoc.entity_id
          );

          if (!match) return;

          if (typeof rpcAssoc.entity_name === 'string' && rpcAssoc.entity_name.trim().length) {
            match.entityName = rpcAssoc.entity_name.trim();
          }
        });
      })
    );
  }
};

const fetchInsightMetrics = async (
  objectType: 'campaign' | 'adset' | 'ad',
  objectIds: string[],
  filters: MetaAlignmentFilters
): Promise<Map<string, MetricsRow>> => fetchMetaInsightMetrics(objectType, objectIds, filters);

const fetchArtistsById = async (ids: string[]): Promise<Map<string, { id: string; name: string }>> => {
  const map = new Map<string, { id: string; name: string }>();
  if (!ids.length) return map;
  const chunkSize = 100;
  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    const { data, error } = await supabase
      .from('artists')
      .select('id, name')
      .in('id', chunk);
    if (error) {
      console.error('Failed to fetch artists for alignment:', error);
      continue;
    }
    (data ?? []).forEach((artist) => {
      map.set(artist.id, { id: artist.id, name: artist.name });
    });
  }
  return map;
};

const fetchLabelsById = async (ids: string[]): Promise<Map<string, { id: string; name: string }>> => {
  const map = new Map<string, { id: string; name: string }>();
  if (!ids.length) return map;
  const chunkSize = 100;
  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    const { data, error } = await supabase
      .from('labels')
      .select('id, name')
      .in('id', chunk);
    if (error) {
      console.error('Failed to fetch labels for alignment:', error);
      continue;
    }
    (data ?? []).forEach((label) => {
      map.set(label.id, { id: label.id, name: label.name });
    });
  }
  return map;
};

const buildSuggestions = (
  campaigns: CampaignRow[],
  associationsByCampaign: Map<string, MetaAlignmentAssociation[]>,
  releaseContexts: Map<string, ReleaseContext>
): Map<string, MetaAlignmentSuggestion[]> => {
  const suggestionsByCampaign = new Map<string, MetaAlignmentSuggestion[]>();

  const releases = Array.from(releaseContexts.values());
  campaigns.forEach((campaign) => {
    const existingAssociations = associationsByCampaign.get(campaign.id) ?? [];
    const hasReleaseAssociation = existingAssociations.some((assoc) => assoc.entityType === 'release');
    if (hasReleaseAssociation) {
      suggestionsByCampaign.set(campaign.id, []);
      return;
    }

    const reasonsList: Array<{ release: ReleaseContext; reasons: MetaAlignmentSuggestionReason[] }> = [];

    releases.forEach((release) => {
      const reasons = mergeReasons([
        computeNameMatchScore(campaign.name, release.title),
        computeArtistSignal(campaign, release),
        computeDateAffinityScore(campaign, release),
      ]);

      if (!reasons.length) return;
      const score = computeSuggestionScore(reasons);
      if (score < 15) return;
      reasonsList.push({ release, reasons });
    });

    const sorted = reasonsList
      .sort((a, b) => computeSuggestionScore(b.reasons) - computeSuggestionScore(a.reasons))
      .slice(0, DEFAULT_SUGGESTION_LIMIT)
      .map(({ release, reasons }) => ({
        releaseId: release.id,
        releaseTitle: release.title,
        releaseDate: release.release_date ?? undefined,
        artistId: release.artist_id ?? undefined,
        artistName: release.artist_name ?? undefined,
        contactId: release.primary_contact_id ?? undefined,
        contactName: release.primary_contact_name ?? undefined,
        score: computeSuggestionScore(reasons),
        reasons,
      }));

    suggestionsByCampaign.set(campaign.id, sorted);
  });

  return suggestionsByCampaign;
};

export const metaAlignmentService = {
  async fetchAlignment(filters: MetaAlignmentFilters = {}): Promise<MetaAlignmentResult> {
    const limit = filters.limit && filters.limit > 0 ? Math.min(filters.limit, 100) : DEFAULT_LIMIT;

    let campaignQuery = supabase
      .from('meta_campaigns')
      .select('id, campaign_id, account_id, name, status, objective, start_time, stop_time, updated_at, created_at')
      .order('updated_at', { ascending: false })
      .limit(limit);

    if (filters.accountId) {
      campaignQuery = campaignQuery.eq('account_id', filters.accountId);
    }
    if (filters.status) {
      campaignQuery = campaignQuery.eq('status', filters.status);
    }
    if (filters.dateFrom) {
      campaignQuery = campaignQuery.gte('updated_at', filters.dateFrom);
    }
    if (filters.dateTo) {
      campaignQuery = campaignQuery.lte('updated_at', filters.dateTo);
    }
    if (filters.search) {
      const pattern = `%${filters.search.trim()}%`;
      campaignQuery = campaignQuery.or(`name.ilike.${pattern},campaign_id.ilike.${pattern}`);
    }

    const { data: campaignRows, error: campaignError } = await campaignQuery;
    if (campaignError) {
      throw new Error(`Failed to load Meta campaigns: ${campaignError.message}`);
    }

    const campaigns = (campaignRows ?? []) as CampaignRow[];
    if (!campaigns.length) {
      return {
        campaigns: [],
        totalCampaigns: 0,
        unmatchedCampaigns: 0,
        filters,
      };
    }

    const campaignIds = campaigns.map((campaign) => campaign.id);
    const campaignExternalIds = campaigns.map((campaign) => campaign.campaign_id);

    const campaignMetricsMap = await fetchInsightMetrics('campaign', campaignExternalIds, filters);

    let adsetRows: AdSetRow[] = [];
    if (campaignExternalIds.length) {
      const { data: adSetData, error: adSetError } = await supabase
        .from('meta_ad_sets')
        .select('id, adset_id, campaign_id, account_id, name, status, updated_at, created_at')
        .in('campaign_id', campaignExternalIds)
        .limit(MAX_ADSETS);
      if (adSetError) {
        console.error('Failed to load ad sets for alignment:', adSetError);
      } else {
        adsetRows = (adSetData ?? []) as AdSetRow[];
      }
    }

    const adsetExternalIds = adsetRows.map((row) => row.adset_id);

    let adRows: AdRow[] = [];
    if (adsetExternalIds.length) {
      const { data: adData, error: adError } = await supabase
        .from('meta_ads')
        .select('id, ad_id, adset_id, campaign_id, account_id, name, status, updated_at, created_at')
        .in('adset_id', adsetExternalIds)
        .limit(MAX_ADS);
      if (adError) {
        console.error('Failed to load ads for alignment:', adError);
      } else {
        adRows = (adData ?? []) as AdRow[];
      }
    }

    const adsetMetricsMap = await fetchInsightMetrics('adset', adsetExternalIds, filters);

    const adExternalIds = adRows.map((row) => row.ad_id);
    const adMetricsMap = await fetchInsightMetrics('ad', adExternalIds, filters);

    const { data: associationRows, error: associationError } = await supabase
      .from('campaign_associations')
      .select('campaign_id, entity_type, entity_id, association_type, notes, created_at')
      .eq('campaign_type', 'meta')
      .in('campaign_id', campaignIds);
    if (associationError) {
      console.error('Failed to load campaign associations for alignment:', associationError);
    }

    const associations = (associationRows ?? []) as AssociationRow[];

    const releaseIdsFromAssociations = associations
      .filter((assoc) => assoc.entity_type === 'release')
      .map((assoc) => assoc.entity_id);

    const artistIdsFromAssociations = associations
      .filter((assoc) => assoc.entity_type === 'artist')
      .map((assoc) => assoc.entity_id);

    const labelIdsFromAssociations = associations
      .filter((assoc) => assoc.entity_type === 'label')
      .map((assoc) => assoc.entity_id);

    const releaseContexts = await fetchReleaseContexts(releaseIdsFromAssociations);
    const artistMap = await fetchArtistsById(artistIdsFromAssociations);
    const labelMap = await fetchLabelsById(labelIdsFromAssociations);

    const associationsByCampaign = buildAssociations(associations, releaseContexts, artistMap, labelMap);
    await enrichAssociationsWithRpc(campaignIds, associationsByCampaign);
    const suggestionsByCampaign = buildSuggestions(campaigns, associationsByCampaign, releaseContexts);

    const adSetsByCampaign = new Map<string, MetaAlignmentAdSet[]>();
    adsetRows.forEach((row) => {
      const metrics = buildMetrics(adsetMetricsMap.get(row.adset_id));
      const adSet: MetaAlignmentAdSet = {
        id: row.id,
        adsetId: row.adset_id,
        campaignId: row.campaign_id,
        name: row.name,
        status: row.status,
        metrics,
        createdAt: row.created_at ?? undefined,
        updatedAt: row.updated_at ?? undefined,
        ads: [],
      };
      const list = adSetsByCampaign.get(row.campaign_id) ?? [];
      list.push(adSet);
      adSetsByCampaign.set(row.campaign_id, list);
    });

    const adByAdset = new Map<string, MetaAlignmentAd[]>();
    adRows.forEach((row) => {
      const metrics = buildMetrics(adMetricsMap.get(row.ad_id));
      const ad: MetaAlignmentAd = {
        id: row.id,
        adId: row.ad_id,
        adsetId: row.adset_id,
        name: row.name,
        status: row.status,
        metrics,
        createdAt: row.created_at ?? undefined,
        updatedAt: row.updated_at ?? undefined,
      };
      const list = adByAdset.get(row.adset_id) ?? [];
      list.push(ad);
      adByAdset.set(row.adset_id, list);
    });

    adSetsByCampaign.forEach((adSets) => {
      adSets.forEach((adSet) => {
        adSet.ads = adByAdset.get(adSet.adsetId) ?? [];
      });
    });

    let alignmentCampaigns: MetaAlignmentCampaign[] = campaigns.map((row) => {
      const adSets = adSetsByCampaign.get(row.campaign_id) ?? [];
      const associations = associationsByCampaign.get(row.id) ?? [];
      const suggestions = suggestionsByCampaign.get(row.id) ?? [];
      const metrics = buildMetrics(campaignMetricsMap.get(row.campaign_id));

      return {
        id: row.id,
        externalId: row.campaign_id,
        accountId: row.account_id,
        name: row.name,
        status: row.status,
        objective: row.objective ?? undefined,
        startTime: row.start_time ?? undefined,
        stopTime: row.stop_time ?? undefined,
        createdAt: row.created_at ?? undefined,
        updatedAt: row.updated_at ?? undefined,
        metrics,
        adSets,
        associations,
        suggestions,
      };
    });

    const unmatchedCampaigns = alignmentCampaigns.filter((campaign) => !campaign.associations.length).length;

    if (filters.needsReview) {
      alignmentCampaigns = alignmentCampaigns.filter((campaign) => !campaign.associations.length);
    }

    return {
      campaigns: alignmentCampaigns,
      totalCampaigns: alignmentCampaigns.length,
      unmatchedCampaigns,
      filters,
    };
  },
};
